/**
 * DealsListPage — alternate table view of deals. Useful when the
 * Kanban gets crowded or you want to filter/sort across stages.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, List } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import { useFiasLeadsDeals, useFiasLeadsPipelines } from '../hooks/useFiasLeads';
import type { FiasLeadsDealStatus } from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';
import { formatCurrency, formatRelativeTime } from '../format';
import { DEAL_SORT_OPTIONS, useDealSort } from '../deal-sort';

interface Props {
  holderId: string;
}

export function DealsListPage({ holderId }: Props) {
  const [pipelineId, setPipelineId] = useState<string>('');
  const [status, setStatus] = useState<FiasLeadsDealStatus | ''>('open');
  const { sortValue, setSortValue, sort } = useDealSort(holderId);

  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const dealsQuery = useFiasLeadsDeals(holderId, {
    pipelineId: pipelineId || undefined,
    status: status || undefined,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
  });

  const pipelines = pipelinesQuery.data ?? [];
  const deals = dealsQuery.data ?? [];

  const stageById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pipelines) for (const s of p.stages) m.set(s.id, s.name);
    return m;
  }, [pipelines]);

  return (
    <div className="p-6 max-w-6xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Deals</h1>
          <p className="text-sm text-muted-foreground">Filter across all stages of a pipeline.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={pipelineId}
            onChange={(e) => setPipelineId(e.target.value)}
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            <option value="">All pipelines</option>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as FiasLeadsDealStatus | '')}
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="">All statuses</option>
          </select>
          <select
            value={sortValue}
            onChange={(e) => setSortValue(e.target.value)}
            aria-label="Sort deals"
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            {DEAL_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Sort: {o.label}
              </option>
            ))}
          </select>
          <Link
            to={`${ARCHE_PATH}/deals`}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
          >
            <LayoutGrid className="h-4 w-4" />
            Board
          </Link>
          <div className="inline-flex items-center gap-2 rounded bg-muted/40 px-3 py-2 text-sm">
            <List className="h-4 w-4" />
            List
          </div>
        </div>
      </header>

      {dealsQuery.isLoading && <SkeletonBlock lines={6} />}

      <div className="rounded border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Deal</th>
              <th className="text-left px-4 py-2 font-medium">Stage</th>
              <th className="text-left px-4 py-2 font-medium">Value</th>
              <th className="text-left px-4 py-2 font-medium">Last activity</th>
              <th className="text-left px-4 py-2 font-medium">Close</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {deals.map((d) => (
              <tr key={d.id} className="hover:bg-muted/30">
                <td className="px-4 py-2">
                  <Link
                    to={`${ARCHE_PATH}/deals/${d.id}`}
                    className="text-foreground hover:underline"
                  >
                    {d.name}
                  </Link>
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {stageById.get(d.stage_id) ?? d.stage_id}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {formatCurrency(d.value, d.currency)}
                </td>
                <td
                  className="px-4 py-2 text-muted-foreground text-xs"
                  title={
                    d.last_activity_at
                      ? new Date(d.last_activity_at).toLocaleString()
                      : 'No activity logged'
                  }
                >
                  {formatRelativeTime(d.last_activity_at)}
                </td>
                <td className="px-4 py-2 text-muted-foreground text-xs">
                  {d.expected_close_date
                    ? new Date(d.expected_close_date).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-4 py-2 text-xs">
                  <span
                    className={
                      'rounded px-2 py-0.5 ' +
                      (d.status === 'won'
                        ? 'bg-emerald-100 text-emerald-700'
                        : d.status === 'lost'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-blue-100 text-blue-700')
                    }
                  >
                    {d.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{d.risk_level ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {deals.length === 0 && !dealsQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            No deals match these filters.
          </p>
        )}
      </div>
    </div>
  );
}
